import { Plus } from "lucide-react";
import { TextField, TextArea, ImageField, SectionCard, RemoveBtn } from "./fields";

export default function ContentEditor({ content, update }) {
  // update(path, value) where path is dot/array notation handled via setters below.
  const set = (section, key, value) => {
    update({ ...content, [section]: { ...content[section], [key]: value } });
  };
  const setArrayItem = (section, arrKey, idx, key, value) => {
    const arr = [...content[section][arrKey]];
    arr[idx] = { ...arr[idx], [key]: value };
    set(section, arrKey, arr);
  };
  const addArrayItem = (section, arrKey, item) => {
    set(section, arrKey, [...(content[section][arrKey] || []), item]);
  };
  const removeArrayItem = (section, arrKey, idx) => {
    set(section, arrKey, content[section][arrKey].filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6">
      {/* HERO */}
      <SectionCard title="Hero" description="Encabezado principal del sitio.">
        <TextField label="Bajada superior" value={content.hero.overline} onChange={(v) => set("hero", "overline", v)} testid="edit-hero-overline" />
        <div className="grid sm:grid-cols-3 gap-4">
          <TextField label="Línea 1" value={content.hero.line1} onChange={(v) => set("hero", "line1", v)} testid="edit-hero-line1" />
          <TextField label="Línea 2" value={content.hero.line2} onChange={(v) => set("hero", "line2", v)} testid="edit-hero-line2" />
          <TextField label="Línea 3 (acento)" value={content.hero.line3} onChange={(v) => set("hero", "line3", v)} testid="edit-hero-line3" />
        </div>
        <TextArea label="Subtítulo" value={content.hero.subtitle} onChange={(v) => set("hero", "subtitle", v)} testid="edit-hero-subtitle" />
        <TextField label="Texto del botón" value={content.hero.cta} onChange={(v) => set("hero", "cta", v)} testid="edit-hero-cta" />
      </SectionCard>

      {/* ABOUT */}
      <SectionCard title="¿Quiénes somos?" description="Sección sobre la consultora.">
        <TextField label="Bajada superior" value={content.about.overline} onChange={(v) => set("about", "overline", v)} testid="edit-about-overline" />
        <TextField label="Título" value={content.about.title} onChange={(v) => set("about", "title", v)} testid="edit-about-title" />
        <TextArea label="Descripción" value={content.about.body} onChange={(v) => set("about", "body", v)} testid="edit-about-body" />
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Estadísticas</span>
            <button type="button" onClick={() => addArrayItem("about", "stats", { value: "", label: "" })} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 space-y-3">
            {(content.about.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-2 gap-3 rounded-lg border border-zinc-200 p-3">
                <TextField label="Valor" value={s.value} onChange={(v) => setArrayItem("about", "stats", i, "value", v)} />
                <TextField label="Etiqueta" value={s.label} onChange={(v) => setArrayItem("about", "stats", i, "label", v)} />
                <div className="col-span-2"><RemoveBtn onClick={() => removeArrayItem("about", "stats", i)} /></div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* SERVICES */}
      <SectionCard title="¿Qué hacemos?" description="Servicios de la consultora.">
        <TextField label="Bajada superior" value={content.services.overline} onChange={(v) => set("services", "overline", v)} testid="edit-services-overline" />
        <TextField label="Título" value={content.services.title} onChange={(v) => set("services", "title", v)} testid="edit-services-title" />
        <TextArea label="Introducción" value={content.services.intro} onChange={(v) => set("services", "intro", v)} testid="edit-services-intro" />
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Servicios</span>
            <button type="button" onClick={() => addArrayItem("services", "items", { number: "", title: "", description: "" })} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 space-y-3">
            {(content.services.items || []).map((it, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <TextField label="Nº" value={it.number} onChange={(v) => setArrayItem("services", "items", i, "number", v)} />
                  <div className="col-span-3"><TextField label="Título" value={it.title} onChange={(v) => setArrayItem("services", "items", i, "title", v)} /></div>
                </div>
                <TextArea label="Descripción" rows={2} value={it.description} onChange={(v) => setArrayItem("services", "items", i, "description", v)} />
                <RemoveBtn onClick={() => removeArrayItem("services", "items", i)} />
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* EXPERIENCE */}
      <SectionCard title="Experiencia" description="Videos de YouTube e imágenes de la experiencia.">
        <TextField label="Bajada superior" value={content.experience.overline} onChange={(v) => set("experience", "overline", v)} testid="edit-experience-overline" />
        <TextField label="Título" value={content.experience.title} onChange={(v) => set("experience", "title", v)} testid="edit-experience-title" />
        <TextArea label="Descripción" value={content.experience.body} onChange={(v) => set("experience", "body", v)} testid="edit-experience-body" />
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Videos (YouTube)</span>
            <button type="button" onClick={() => addArrayItem("experience", "videos", { title: "", youtube_url: "" })} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 space-y-3">
            {(content.experience.videos || []).map((v, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-3">
                <TextField label="Título" value={v.title} onChange={(val) => setArrayItem("experience", "videos", i, "title", val)} testid={`edit-video-title-${i}`} />
                <TextField label="URL de YouTube" value={v.youtube_url} onChange={(val) => setArrayItem("experience", "videos", i, "youtube_url", val)} testid={`edit-video-url-${i}`} placeholder="https://www.youtube.com/watch?v=..." />
                <RemoveBtn onClick={() => removeArrayItem("experience", "videos", i)} testid={`remove-video-${i}`} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Galería de imágenes</span>
            <button type="button" onClick={() => addArrayItem("experience", "gallery", "")} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium" data-testid="add-gallery-image"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 space-y-3">
            {(content.experience.gallery || []).map((img, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-2">
                <ImageField label={`Imagen ${i + 1}`} value={img} onChange={(val) => { const arr = [...content.experience.gallery]; arr[i] = val; set("experience", "gallery", arr); }} testid={`gallery-image-${i}`} />
                <RemoveBtn onClick={() => set("experience", "gallery", content.experience.gallery.filter((_, x) => x !== i))} testid={`remove-gallery-${i}`} />
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* TEAM */}
      <SectionCard title="Equipo" description="Integrantes del equipo.">
        <TextField label="Bajada superior" value={content.team.overline} onChange={(v) => set("team", "overline", v)} testid="edit-team-overline" />
        <TextField label="Título" value={content.team.title} onChange={(v) => set("team", "title", v)} testid="edit-team-title" />
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Integrantes</span>
            <button type="button" onClick={() => addArrayItem("team", "members", { name: "", role: "", background: "", photo: "" })} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium" data-testid="add-team-member"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 space-y-3">
            {(content.team.members || []).map((m, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-3">
                <TextField label="Nombre" value={m.name} onChange={(v) => setArrayItem("team", "members", i, "name", v)} testid={`team-name-${i}`} />
                <TextField label="Cargo" value={m.role} onChange={(v) => setArrayItem("team", "members", i, "role", v)} testid={`team-role-${i}`} />
                <TextField label="Formación" value={m.background} onChange={(v) => setArrayItem("team", "members", i, "background", v)} testid={`team-bg-${i}`} />
                <ImageField label="Foto" value={m.photo} onChange={(v) => setArrayItem("team", "members", i, "photo", v)} testid={`team-photo-${i}`} />
                <RemoveBtn onClick={() => removeArrayItem("team", "members", i)} testid={`remove-member-${i}`} />
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* CLIENTS */}
      <SectionCard title="Clientes" description="Testimonios y logos de clientes.">
        <TextField label="Bajada superior" value={content.clients.overline} onChange={(v) => set("clients", "overline", v)} testid="edit-clients-overline" />
        <TextField label="Título" value={content.clients.title} onChange={(v) => set("clients", "title", v)} testid="edit-clients-title" />
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Testimonios</span>
            <button type="button" onClick={() => addArrayItem("clients", "testimonials", { quote: "", name: "", role: "" })} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 space-y-3">
            {(content.clients.testimonials || []).map((t, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-3">
                <TextArea label="Cita" value={t.quote} onChange={(v) => setArrayItem("clients", "testimonials", i, "quote", v)} />
                <div className="grid grid-cols-2 gap-3">
                  <TextField label="Nombre" value={t.name} onChange={(v) => setArrayItem("clients", "testimonials", i, "name", v)} />
                  <TextField label="Cargo / Empresa" value={t.role} onChange={(v) => setArrayItem("clients", "testimonials", i, "role", v)} />
                </div>
                <RemoveBtn onClick={() => removeArrayItem("clients", "testimonials", i)} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Logos (nombres)</span>
            <button type="button" onClick={() => set("clients", "logos", [...(content.clients.logos || []), ""])} className="inline-flex items-center gap-1 text-xs text-jauja-olive font-medium"><Plus size={14} /> Añadir</button>
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {(content.clients.logos || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={l} onChange={(e) => { const arr = [...content.clients.logos]; arr[i] = e.target.value; set("clients", "logos", arr); }} className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-jauja-olive" />
                <button type="button" onClick={() => set("clients", "logos", content.clients.logos.filter((_, x) => x !== i))} className="text-red-400 hover:text-red-600 text-xs">✕</button>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* CONTACT */}
      <SectionCard title="Contacto" description="Datos de contacto y textos de la sección final.">
        <TextField label="Título" value={content.contact.heading} onChange={(v) => set("contact", "heading", v)} testid="edit-contact-heading" />
        <TextField label="Subtítulo" value={content.contact.subheading} onChange={(v) => set("contact", "subheading", v)} testid="edit-contact-subheading" />
        <TextArea label="Descripción" value={content.contact.body} onChange={(v) => set("contact", "body", v)} testid="edit-contact-body" />
        <div className="grid sm:grid-cols-3 gap-4">
          <TextField label="Dirección" value={content.contact.address} onChange={(v) => set("contact", "address", v)} testid="edit-contact-address" />
          <TextField label="Teléfono" value={content.contact.phone} onChange={(v) => set("contact", "phone", v)} testid="edit-contact-phone" />
          <TextField label="Email" value={content.contact.email} onChange={(v) => set("contact", "email", v)} testid="edit-contact-email" />
        </div>
      </SectionCard>
    </div>
  );
}
